import React, { useState, useEffect } from 'react';
import { transferAPI, deviceRequestAPI } from '../services/api';

const Sidebar = ({ currentView, onViewChange, isCollapsed, onToggleCollapse, user, onLogout }) => {
  const [transferCount, setTransferCount] = useState(0);
  const [deviceRequestCount, setDeviceRequestCount] = useState(0);
  const isSuperAdmin = user?.role === 'super_admin';
  const isDeviceManager = user?.role === 'device_manager';
  const canManageDevices = isSuperAdmin || isDeviceManager;

  useEffect(() => {
    const loadCounts = async () => {
      try {
        const [transferRes, requestRes] = await Promise.all([
          transferAPI.getTransferCount(),
          deviceRequestAPI.getRequestCount()
        ]);
        setTransferCount(transferRes.data.count || 0);
        setDeviceRequestCount(requestRes.data.count || 0);
      } catch (error) {
        console.error('Error loading counts:', error);
      }
    };

    if (user) {
      loadCounts();
      // Refresh count every 30 seconds
      const interval = setInterval(loadCounts, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: 'fa-home' },
    { id: 'devices', label: 'Devices', icon: 'fa-mobile-alt' },
    ...(canManageDevices ? [{ id: 'register', label: 'Register Device', icon: 'fa-plus-circle' }] : []),
    { id: 'transfer-requests', label: 'Assignment Requests', icon: 'fa-exchange-alt', badge: transferCount },
    { id: 'device-requests', label: 'Device Requests', icon: 'fa-clipboard-list', badge: deviceRequestCount },
    { id: 'assign', label: 'History', icon: 'fa-history' },
    { id: 'stats', label: 'Statistics', icon: 'fa-chart-bar' },
    ...(isSuperAdmin ? [{ id: 'email-scheduler', label: 'Email Scheduler', icon: 'fa-envelope-open-text' }] : []),
    ...(isSuperAdmin ? [{ id: 'settings', label: 'Settings', icon: 'fa-cog' }] : []),
    ...(canManageDevices ? [{ id: 'device-managers', label: 'User Management', icon: 'fa-user-cog' }] : []),
  ];

  return (
    <div className={`sidebar ${isCollapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        {!isCollapsed && (
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            alignItems: 'center', 
            justifyContent: 'center',
            gap: '0.75rem',
            width: '100%'
          }}>
            <img 
              src="/trenser-logo.png" 
              alt="Trenser Logo" 
              style={{ 
                width: '120px', 
                height: 'auto',
                objectFit: 'contain'
              }} 
            />
            <h2 style={{ margin: 0, fontSize: '1.125rem', fontWeight: 600, color: 'var(--light-text)' }}>
              Device Fleet
            </h2>
          </div>
        )}
        {isCollapsed && (
          <div style={{
            fontSize: '1.5rem',
            fontWeight: 600,
            color: 'var(--light-text)',
            textAlign: 'center'
          }}>
            📱
          </div>
        )}
        <button 
          className="sidebar-toggle"
          onClick={onToggleCollapse}
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? '☰' : '✕'}
        </button>
      </div>
      
      <nav className="sidebar-nav">
        {menuItems.map((item) => (
          <button
            key={item.id}
            className={`nav-item ${currentView === item.id ? 'active' : ''}`}
            onClick={() => onViewChange(item.id)}
            title={item.label}
          >
            <span className="nav-icon">
              <i className={`fas ${item.icon}`} aria-hidden="true"></i>
            </span>
            {!isCollapsed && (
              <span className="nav-label">
                {item.label}
                {item.badge > 0 && (
                  <span className="badge" style={{
                    marginLeft: '0.5rem',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    padding: '0.15rem 0.5rem',
                    borderRadius: '12px',
                    fontSize: '0.75rem',
                    fontWeight: 600
                  }}>
                    {item.badge}
                  </span>
                )}
              </span>
            )}
            {isCollapsed && item.badge > 0 && (
              <span style={{
                position: 'absolute',
                top: '8px',
                right: '8px',
                backgroundColor: '#dc2626',
                color: 'white',
                borderRadius: '50%',
                width: '18px',
                height: '18px',
                fontSize: '0.625rem',
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                {item.badge > 9 ? '9+' : item.badge}
              </span>
            )}
          </button>
        ))}
      </nav>

      {user && (
        <div className="sidebar-footer">
          {!isCollapsed && (
            <div className="user-info">
              <div className="user-avatar-container">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="user-avatar" />
                ) : (
                  <div className="user-avatar-placeholder">
                    <i className="fas fa-user"></i>
                  </div>
                )}
                {/* Role Icon Badge */}
                {user.role && user.role !== 'super_admin' && (
                  <div className={`role-badge ${user.role === 'device_manager' ? 'manager' : 'employee'}`} title={user.role === 'device_manager' ? 'Device Manager' : 'Employee'}>
                    <i className={`fas ${user.role === 'device_manager' ? 'fa-user-tie' : 'fa-user'}`}></i>
                  </div>
                )}
                {user.role === 'super_admin' && (
                  <div className="role-badge super-admin" title="Super Admin">
                    <i className="fas fa-crown"></i>
                  </div>
                )}
              </div>
              <div className="user-details">
                <div className="user-name">{user.name && user.name !== 'Pending login' ? user.name : user.email}</div>
                <div className="user-email">{user.email}</div>
                {user.deliveryUnit && (
                  <div className="user-delivery-unit">
                    <i className="fas fa-building" style={{ marginRight: '0.25rem', fontSize: '0.7rem' }}></i>
                    {user.deliveryUnit}
                  </div>
                )}
              </div>
            </div>
          )}
          {isCollapsed && (
            <div className="user-info">
              <div className="user-avatar-container">
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="user-avatar" title={`${user.name || user.email}${user.deliveryUnit ? ` - ${user.deliveryUnit}` : ''}`} />
                ) : (
                  <div className="user-avatar-placeholder" title={`${user.name || user.email}${user.deliveryUnit ? ` - ${user.deliveryUnit}` : ''}`}>
                    <i className="fas fa-user"></i>
                  </div>
                )}
                {/* Role Icon Badge */}
                {user.role && user.role !== 'super_admin' && (
                  <div className={`role-badge ${user.role === 'device_manager' ? 'manager' : 'employee'}`} title={user.role === 'device_manager' ? 'Device Manager' : 'Employee'}>
                    <i className={`fas ${user.role === 'device_manager' ? 'fa-user-tie' : 'fa-user'}`}></i>
                  </div>
                )}
                {user.role === 'super_admin' && (
                  <div className="role-badge super-admin" title="Super Admin">
                    <i className="fas fa-crown"></i>
                  </div>
                )}
              </div>
            </div>
          )}
          <button
            className="logout-button"
            onClick={onLogout}
            title="Logout"
          >
            <span className="nav-icon">
              <i className="fas fa-sign-out-alt" aria-hidden="true"></i>
            </span>
            {!isCollapsed && <span className="nav-label">Logout</span>}
          </button>
        </div>
      )}
    </div>
  );
};

export default Sidebar;
